import { Autorenew, CalendarMonth } from "@mui/icons-material";
import { alpha, Box, Card, Fab, Grid, ListItemText, Popover, Stack, Typography } from "@mui/material";
import { DateCalendar, LocalizationProvider, PickersDay } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isEqual, isMonday, isSunday, isWithinInterval, startOfDay } from "date-fns";
import frLocale from "date-fns/locale/en-IN";
import { AnimatePresence, motion } from "framer-motion";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DATE_TIME_FORMAT } from "../../app/constants/common";
import { getDateReferenceString, highlightText, serverToUi, uiToServer } from "../../app/utils/utilityFunctions";
import { StatisticBoard, UiIconButton } from "../../components/ui/UiUtils";
import { useGetAttendanceStatsMutation } from "../../hooks/ApiHooks";
import { MuiTooltip } from "../../styled/commonStyles";
import { StyledChip } from "../../styled/tableStyles";
import { attendanceStatsConfig } from "./config/attendanceUtils";
import {
  selectAttendanceDateRange,
  selectAttendanceDateRangeInDateFormat,
  selectAttendanceDisplayDateLabel,
  selectHoveredDayInDateFormat,
  setDateRange,
  setHoveredDay,
} from "./slice/attendanceSlice";

export const renderAttendanceStatus = (mapping) => (data, searchStr) => {
  if (!mapping[data]) return "N/A";
  return <StyledChip label={highlightText(mapping[data].label, searchStr)} color={mapping[data].color || "primary"} />;
};

export const RenderInOutDateTime = ({ date, time, referenceDate }) => {
  return (
    <MuiTooltip title={serverToUi(date)} fontSize={10}>
      <ListItemText primary={serverToUi(time, DATE_TIME_FORMAT.UI_TIME_FORMAT)} secondary={getDateReferenceString(date, referenceDate)} />
    </MuiTooltip>
  );
};

export const AttendanceStatistics = () => {
  const displayAttendanceDateLabel = useSelector(selectAttendanceDisplayDateLabel);
  const [statConfig, setStatConfig] = useState(attendanceStatsConfig({}));
  const dateValue = useSelector(selectAttendanceDateRange);
  const { triggerMutation, isLoading, data } = useGetAttendanceStatsMutation();

  const refetchData = useCallback(() => {
    triggerMutation(dateValue[0], dateValue[1]);
  }, [triggerMutation, dateValue]);

  useEffect(() => {
    refetchData();
  }, [refetchData]);

  useEffect(() => {
    if (!isLoading && data?.data) {
      setStatConfig(attendanceStatsConfig(data.data));
    }
  }, [isLoading, data?.data]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Stack direction="row" rowGap={2} justifyContent="space-between" alignItems="center" sx={{ my: 1 }}>
        <ListItemText
          primary="Dashboard Statistics"
          secondary={displayAttendanceDateLabel}
          primaryTypographyProps={{ noWrap: true, fontSize: "1.24rem", fontWeight: 600, color: "primary" }}
          secondaryTypographyProps={{ variant: "h6", color: "text.primary" }}
        />
        <UiIconButton title="Reload Stats" onClickIcon={refetchData} loading={isLoading}>
          <Autorenew />
        </UiIconButton>
      </Stack>
      <Grid container spacing={2}>
        {Object.values(statConfig).map(({ id, ...restProps }, index) => (
          <Grid key={id} item xs={12} sm={6} lg={index === Object.values(statConfig).length - 1 ? 12 : 4}>
            <StatisticBoard key={id} loading={isLoading} {...restProps} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const CustomPickersDay = memo(({ day, _selected, ...other }) => {
  const [startDate, endDate] = useSelector(selectAttendanceDateRangeInDateFormat);
  const hoveredDate = useSelector(selectHoveredDayInDateFormat);
  const dispatch = useDispatch();

  const selectedRangeBoolean = useMemo(() => {
    return {
      isRangeStart: startDate && isEqual(startOfDay(day), startOfDay(startDate)),
      isRangeEnd: endDate && isEqual(startOfDay(day), startOfDay(endDate)),
      isInRange: startDate && endDate && isWithinInterval(day, { start: startDate, end: endDate }),
      isMonday: isMonday(day),
      isSunday: isSunday(day),
    };
  }, [startDate, endDate, day]);

  const inRangeSelectedProps = useMemo(() => {
    const { isInRange, isRangeEnd, isRangeStart, isMonday, isSunday } = selectedRangeBoolean;
    const backgroundColor = (theme) => (isInRange ? alpha(theme.palette.primary.main, 0.2) : "transparent");
    const borderRadius = (() => {
      if (isRangeStart || (isInRange && isMonday && !isRangeEnd)) {
        return "50% 0 0 50%";
      }
      if (isRangeEnd || (isInRange && isSunday)) {
        return "0 50% 50% 0";
      }
      if (isInRange) {
        return "0";
      }
      return "50%";
    })();
    const hoverBackground = (theme) => (isInRange ? alpha(theme.palette.primary.main, 0.3) : undefined);

    return { backgroundColor, borderRadius, "&:hover": { backgroundColor: hoverBackground } };
  }, [selectedRangeBoolean]);

  const dottedBorder = useMemo(() => {
    const noBorder = { border: "none" };
    const dashedBorder = {
      border: (theme) => `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
      borderRadius: "50%",
    };

    if (!hoveredDate || (!startDate && !endDate) || (startDate && endDate)) return noBorder;

    const [start, end] = startDate ? [startDate, hoveredDate] : [hoveredDate, endDate];
    const isInRange = start && end && (start <= end ? day >= start && day <= end : day >= end && day <= start);

    if (!isInRange) return noBorder;

    if ((startDate && isEqual(startOfDay(startDate), startOfDay(day))) || (endDate && isEqual(startOfDay(endDate), startOfDay(day)))) {
      return noBorder;
    }

    return dashedBorder;
  }, [day, hoveredDate, startDate, endDate]);

  return (
    <PickersDay
      {...other}
      day={day}
      selected={selectedRangeBoolean.isRangeEnd || selectedRangeBoolean.isRangeStart}
      disableMargin
      onMouseEnter={() => dispatch(setHoveredDay(uiToServer(day)))}
      onMouseLeave={() => dispatch(setHoveredDay(null))}
      sx={{
        width: "36px",
        height: "36px",
        margin: 0,
        fontSize: "0.875rem",
        ...inRangeSelectedProps,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          ...dottedBorder,
          pointerEvents: "none",
        },
        "&.Mui-selected": {
          backgroundColor: "primary.main",
          color: "primary.contrastText",
          "&:hover": { backgroundColor: "primary.dark" },
        },
      }}
    />
  );
});

export const ChooseCalendarDate = () => {
  const [startDate, endDate] = useSelector(selectAttendanceDateRangeInDateFormat);
  const dispatch = useDispatch();
  const [currentDateValue, setCurrentDateValue] = useState(endDate || startDate || new Date());

  const handleDateSelect = (value, selectionState) => {
    setCurrentDateValue(value);

    if (selectionState !== "finish") return;

    const updateDateRange = (range) => {
      dispatch(
        setDateRange({
          start: uiToServer(range.start, DATE_TIME_FORMAT.SERVER_DATE),
          end: uiToServer(range.end, DATE_TIME_FORMAT.SERVER_DATE),
        })
      );
    };

    const selectedDate = startOfDay(value);

    /* Unselect start date */
    if (isEqual(selectedDate, startDate)) {
      updateDateRange({ start: null, end: endDate });
      return;
    }

    /* Unselect end date */
    if (isEqual(selectedDate, endDate)) {
      updateDateRange({ start: startDate, end: null });
      return;
    }

    /* Start new selection */
    if (!startDate || (startDate && endDate)) {
      updateDateRange({ start: selectedDate, end: null });
    } else if (selectedDate < startDate) {
      updateDateRange({ start: selectedDate, end: startDate });
    } else {
      updateDateRange({ start: startDate, end: selectedDate });
    }
  };

  return (
    <Card
      component={motion.div}
      sx={{
        height: "100%",
        p: 1,
        minWidth: "320px",
        overflow: "hidden",
        background: (theme) =>
          `linear-gradient(220deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
        borderRadius: 2,
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 2, pl: 3, pr: 1.5 }}>
        <Typography fontWeight={600} fontSize="1.24rem" color="primary">
          Select Date Range
        </Typography>
      </Stack>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
        <DateCalendar
          value={currentDateValue}
          onChange={handleDateSelect}
          slots={{ day: CustomPickersDay }}
          showDaysOutsideCurrentMonth
          sx={{
            m: 0,
            height: "auto",
            "& .MuiPickersDay-root, .MuiDayCalendar-weekDayLabel": { fontSize: "0.95rem" },
            "& .MuiPickersSlideTransition-root": { height: "192px" },
            "& .MuiDayCalendar-header": { "& span": { width: "36px", margin: 0 } },
            "& .MuiDateCalendar-root": { width: "320px" },
          }}
          views={["year", "month", "day"]}
          disableFuture
        />
      </LocalizationProvider>
    </Card>
  );
};

const MotionFAB = motion.create(Box);

export const FloatingCalendarButton = ({ show }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <AnimatePresence>
      {!!show && (
        <MotionFAB
          initial={{ y: -100, opacity: 0 }}
          animate={{
            y: 0,
            opacity: 1,
            transition: {
              type: "spring",
              stiffness: 200,
              damping: 20,
              mass: 1,
            },
          }}
          exit={{
            y: 100,
            opacity: 0,
            transition: {
              duration: 0.3,
              ease: "easeInOut",
            },
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1000,
          }}
        >
          <MuiTooltip title="Choose Date" placement="left">
            <Fab
              color="primary"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              aria-label="choose date"
              size="large"
              sx={{
                width: 64,
                height: 64,
                backgroundColor: (theme) => theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.primary.dark,
                },
                boxShadow: (theme) => `0 8px 24px ${theme.palette.primary.main}40`,
                border: (theme) => `3px solid ${theme.palette.primary.light}`,
              }}
            >
              <CalendarMonth size={32} />
            </Fab>
          </MuiTooltip>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }}>
              <ChooseCalendarDate />
            </motion.div>
          </Popover>
        </MotionFAB>
      )}
    </AnimatePresence>
  );
};
